import React, { useEffect, useMemo, useState } from "react";
import {
  Modal,
  Input,
  Form,
  Button,
  Typography,
  InputNumber,
  Divider,
  Space,
  Select,
  DatePicker,
  AutoComplete,
} from "antd";
import { PlusOutlined, MinusOutlined } from "@ant-design/icons";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import { api } from "../../config/api";
import { getUsers } from "../../lib/user";
import { getNextInvoiceNumber } from "../../utils/invoiceNumberUtils";

const { Title, Text } = Typography;
const DESCRIPTION_MAX_LENGTH = 200;
const ORDER_REFERENCE_MAX_LENGTH = 60;
const BOTTOM_NOTE_MAX_LENGTH = 180;

const DEFAULT_BANK_ACCOUNT = {
  label: "One Union NL28",
  accountName: "One Union Solutions B.V.",
  iban: "NL28 INGB 0006 9674 14",
  swift: "INGBNL2A",
};

const asCreditNegative = (value) => {
  const numeric = Number(value);
  if (!numeric || Number.isNaN(numeric)) return 0;
  return -Math.abs(numeric);
};

const CreditInvoiceModal = ({
  open,
  onClose,
  invoice,
  createMode = false,
  onDownload,
  onCreated,
  downloading,
}) => {
  const [form] = Form.useForm();
  const [loadingInvoice, setLoadingInvoice] = useState(false);
  const [saving, setSaving] = useState(false);
  const [nameOptions, setNameOptions] = useState([]);
  const [loadingNameOptions, setLoadingNameOptions] = useState(false);
  const itemsWatch = Form.useWatch("items", form);
  const currencyWatch = Form.useWatch("currency", form);
  const vatWatch = Form.useWatch("vatPercentage", form);

  const currency = createMode
    ? currencyWatch || "USD"
    : invoice?.currency || "USD";
  const vatPercentage = createMode
    ? Number(vatWatch || 0)
    : Number(invoice?.vatPercentage || 0);

  const totals = useMemo(() => {
    const items = itemsWatch || [];
    const subTotal = items.reduce((sum, item) => {
      const line = Math.abs(Number(item?.qty || 0)) * Math.abs(Number(item?.price || 0));
      return sum + asCreditNegative(line);
    }, 0);
    const vatAmount = asCreditNegative(
      Math.abs(Number(((Math.abs(subTotal) * vatPercentage) / 100).toFixed(2)))
    );
    const includeInsurance =
      !createMode &&
      !!invoice?.isInsuranceChecked &&
      Math.abs(Number(invoice?.insuranceAmount || 0)) > 0;
    const insurance = includeInsurance
      ? asCreditNegative(invoice.insuranceAmount)
      : 0;
    const total = Number((subTotal + vatAmount + insurance).toFixed(2));
    return {
      subTotal,
      vatAmount,
      vatPercentage,
      insurance,
      total,
    };
  }, [itemsWatch, vatPercentage, invoice, createMode]);

  useEffect(() => {
    if (!open || !createMode) return;

    let cancelled = false;

    const fetchNameOptions = async () => {
      setLoadingNameOptions(true);
      try {
        const [firestoreUsersResult, invoicesResult] = await Promise.allSettled([
          getUsers(),
          api.get("/invoice"),
        ]);

        const namesByKey = new Map();

        const addName = (rawName, user = null) => {
          const name = String(rawName || "").trim();
          if (!name) return;
          const key = name.toLowerCase();
          const existing = namesByKey.get(key);
          // Keep the user record so selecting their name can use its saved
          // Bill To details. Invoice-only names are still available as custom names.
          if (!existing || (user && !existing.user)) {
            namesByKey.set(key, { name, user: user || existing?.user || null });
          }
        };

        if (firestoreUsersResult.status === "fulfilled") {
          (firestoreUsersResult.value || []).forEach((user) => addName(user?.name, user));
        }

        if (invoicesResult.status === "fulfilled") {
          const invoices = invoicesResult.value?.data;
          (Array.isArray(invoices) ? invoices : []).forEach((inv) => addName(inv?.name));
        }

        if (!cancelled) {
          setNameOptions(
            Array.from(namesByKey.values())
              .sort((a, b) => a.name.localeCompare(b.name))
              .map(({ name, user }) => ({ value: name, label: name, user }))
          );
        }
      } finally {
        if (!cancelled) {
          setLoadingNameOptions(false);
        }
      }
    };

    fetchNameOptions();

    return () => {
      cancelled = true;
    };
  }, [open, createMode]);

  const handleCreditCustomerNameChange = (value) => {
    const normalizedName = String(value || "").trim().toLowerCase();
    const selectedUser = nameOptions.find(
      (option) => String(option.value).trim().toLowerCase() === normalizedName
    )?.user;

    form.setFieldsValue({
      name: value,
      // Only use the customer's explicitly saved Bill To value. A custom
      // name or a user without it must leave this field blank.
      externalBillToText: String(selectedUser?.billToText || "").trim(),
    });
  };

  const handleCopyCustomerToServiceRecipient = () => {
    form.setFieldsValue({
      serviceRecipientText: form.getFieldValue("externalBillToText") || "",
    });
  };

  const handleClearServiceRecipient = () => {
    form.setFieldsValue({ serviceRecipientText: "" });
  };

  useEffect(() => {
    if (!open) return;

    if (createMode) {
      (async () => {
        setLoadingInvoice(true);
        try {
          const nextNumber = await getNextInvoiceNumber();
          form.setFieldsValue({
            name: "",
            invoiceNo: nextNumber,
            invoiceDate: dayjs(),
            dueDays: 14,
            currency: "USD",
            vatPercentage: 0,
            orderReference: "",
            collectionLocation: "",
            deliveryLocation: "",
            externalBillToText: "",
            serviceRecipientText: "",
            description: "",
            bottomNote: "",
            items: [{ description: "", qty: -1, price: 0 }],
          });
        } catch (error) {
          toast.error("Failed to prepare credit invoice form");
          form.setFieldsValue({
            items: [{ description: "", qty: -1, price: 0 }],
          });
        } finally {
          setLoadingInvoice(false);
        }
      })();
      return;
    }

    if (!invoice) return;

    (async () => {
      setLoadingInvoice(true);
      try {
        let fresh = invoice;
        const [invoiceResult, nextNumberResult] = await Promise.allSettled([
          invoice?.invoiceNo
            ? api.get(`/invoice/search`, {
                params: { invoiceNo: String(invoice.invoiceNo).trim() },
              })
            : api.get(`/invoice/${invoice._id}`),
          getNextInvoiceNumber(),
        ]);

        if (invoiceResult.status === "fulfilled") {
          fresh = invoiceResult.value?.data || invoice;
        }

        const sourceItems = Array.isArray(fresh?.items) ? fresh.items : [];
        const creditItems = sourceItems.map((item) => ({
          description: item?.description || "",
          qty: asCreditNegative(item?.qty || 1),
          price: asCreditNegative(item?.price || 0),
        }));

        form.setFieldsValue({
          invoiceNo:
            nextNumberResult.status === "fulfilled"
              ? nextNumberResult.value
              : "",
          items: creditItems.length
            ? creditItems
            : [{ description: "", qty: -1, price: 0 }],
        });
      } catch (error) {
        toast.error("Failed to load invoice items");
        const sourceItems = Array.isArray(invoice?.items) ? invoice.items : [];
        form.setFieldsValue({
          items: sourceItems.map((item) => ({
            description: item?.description || "",
            qty: asCreditNegative(item?.qty || 1),
            price: asCreditNegative(item?.price || 0),
          })),
        });
      } finally {
        setLoadingInvoice(false);
      }
    })();
  }, [open, invoice, form, createMode]);

  const buildCreditItems = (values) =>
    (values.items || [])
      .filter((item) => String(item?.description || "").trim())
      .map((item) => ({
        description: String(item.description).trim(),
        qty: asCreditNegative(item.qty),
        price: asCreditNegative(item.price),
      }));

  const buildCreditOverrides = (items) => ({
    items,
    subTotal: totals.subTotal,
    vatAmount: totals.vatAmount,
    vatPercentage: totals.vatPercentage,
    insurance: totals.insurance,
    total: totals.total,
  });

  const handleFinish = async (values) => {
    const items = buildCreditItems(values);

    if (!items.length) {
      toast.error("Add at least one line item");
      return;
    }

    const creditOverrides = buildCreditOverrides(items);

    if (!createMode) {
      const invoiceNo = String(values.invoiceNo || "").trim();
      if (!invoiceNo) {
        toast.error("Credit invoice number is required");
        return;
      }

      setSaving(true);
      try {
        const response = await api.post(`/invoice/${invoice._id}/credit`, {
          invoiceNo,
          ...creditOverrides,
        });
        const createdInvoice = response?.data?.data;
        if (!createdInvoice) {
          throw new Error("Credit invoice was not created");
        }

        onCreated?.(createdInvoice);
        await onDownload?.(creditOverrides, createdInvoice);
        toast.success(`Credit invoice ${createdInvoice.invoiceNo} created successfully!`);
        onClose?.();
      } catch (error) {
        const message =
          error?.response?.data?.message ||
          error?.response?.data?.errors?.[0] ||
          error.message;
        toast.error(message || "Credit invoice not created");
      } finally {
        setSaving(false);
      }
      return;
    }

    const name = String(values.name || "").trim();
    const invoiceNo = String(values.invoiceNo || "").trim();
    const externalBillToText = String(values.externalBillToText || "").trim();
    const description = String(values.description || "").trim();
    const orderReference = String(values.orderReference || "").trim();

    if (!name || !invoiceNo || !externalBillToText || !description || !orderReference) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSaving(true);
    try {
      // Persist absolute amounts for payment math; PDF uses signed creditOverrides
      const absoluteItems = items.map((item) => ({
        description: item.description,
        qty: Math.abs(Number(item.qty || 0)),
        price: Math.abs(Number(item.price || 0)),
      }));
      const absoluteVatAmount = Math.abs(Number(totals.vatAmount || 0));
      const absoluteTotal = Math.abs(Number(totals.total || 0));

      const response = await api.post("/invoice/external", {
        name,
        invoiceNo,
        invoiceDate: values.invoiceDate
          ? dayjs(values.invoiceDate).format("YYYY-MM-DD")
          : dayjs().format("YYYY-MM-DD"),
        dueDays: Number(values.dueDays || 0),
        currency: values.currency || "USD",
        vatPercentage: Number(values.vatPercentage || 0),
        vatAmount: absoluteVatAmount,
        total: absoluteTotal,
        paid: 0,
        status: "Not Paid",
        orderReference,
        collectionLocation: String(values.collectionLocation || "").trim(),
        deliveryLocation: String(values.deliveryLocation || "").trim(),
        externalBillToText,
        serviceRecipientText: String(values.serviceRecipientText || "").trim(),
        description,
        bottomNote: String(values.bottomNote || "").trim(),
        items: absoluteItems,
        selectedBankAccount: DEFAULT_BANK_ACCOUNT,
        invoiceSource: "external",
        isExternalInvoice: true,
        isCreditInvoice: true,
        documentType: "credit",
      });

      const createdInvoice = response?.data?.data;
      if (!createdInvoice) {
        throw new Error("Credit invoice was not created");
      }

      onCreated?.(createdInvoice);
      await onDownload?.(creditOverrides, createdInvoice);
      toast.success("Credit invoice created successfully!");
      onClose?.();
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.errors?.[0] ||
        error.message;
      toast.error(message || "Credit invoice not created");
    } finally {
      setSaving(false);
    }
  };

  const busy = loadingInvoice || downloading || saving;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      title={
        <div>
          <Title level={4} className="!mb-0 text-[#053F92]">
            {createMode ? "Create Credit Invoice" : "Credit Invoice"}
          </Title>
          <Text type="secondary" className="text-sm">
            {createMode
              ? "Create a new credit invoice with negative amounts"
              : invoice?.invoiceNo
                ? `Based on invoice ${invoice.invoiceNo}`
                : "Review and adjust credit line items"}
          </Text>
        </div>
      }
      width={createMode ? 960 : 820}
      style={{ top: 40 }}
      destroyOnClose
      maskClosable={false}
    >
      <div className="mb-4 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
        Remove lines with <strong>-</strong>, or add discount/other lines with <strong>+</strong>.
        On the PDF, only <strong>To settle</strong> stays positive; qty, price, amount and totals
        are shown as negative when non-zero.
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        disabled={busy}
      >
        {!createMode && (
          <Form.Item
            name="invoiceNo"
            label="New Credit Invoice No."
            rules={[{ required: true, message: "Required" }]}
          >
            <Input
              placeholder="Next invoice number"
              className="max-w-xs"
            />
          </Form.Item>
        )}

        {createMode && (
          <>
            <Divider orientation="left" className="!mt-0">
              Invoice details
            </Divider>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-3">
              <Form.Item
                name="name"
                label="Name"
                rules={[{ required: true, message: "Required" }]}
              >
                <AutoComplete
                  className="w-full"
                  options={nameOptions}
                  onChange={handleCreditCustomerNameChange}
                  filterOption={(inputValue, option) =>
                    String(option?.value || "")
                      .toLowerCase()
                      .includes(String(inputValue || "").toLowerCase())
                  }
                  allowClear
                  notFoundContent={
                    loadingNameOptions ? "Loading names..." : "Type a custom name"
                  }
                >
                  <Input
                    placeholder={
                      loadingNameOptions
                        ? "Loading names..."
                        : "Select or enter user / company name"
                    }
                    autoComplete="off"
                  />
                </AutoComplete>
              </Form.Item>
              <Form.Item
                name="invoiceNo"
                label="Invoice No."
                rules={[{ required: true, message: "Required" }]}
              >
                <Input placeholder="Invoice number" />
              </Form.Item>
              <Form.Item
                name="invoiceDate"
                label="Invoice Date"
                rules={[{ required: true, message: "Required" }]}
              >
                <DatePicker className="w-full" format="YYYY-MM-DD" />
              </Form.Item>
              <Form.Item
                name="dueDays"
                label="Due Days"
                rules={[{ required: true, message: "Required" }]}
              >
                <InputNumber className="w-full" min={1} step={1} />
              </Form.Item>
              <Form.Item
                name="currency"
                label="Currency"
                rules={[{ required: true, message: "Required" }]}
              >
                <Select
                  options={[
                    { value: "USD", label: "USD" },
                    { value: "EUR", label: "EUR" },
                  ]}
                />
              </Form.Item>
              <Form.Item name="vatPercentage" label="VAT %">
                <InputNumber className="w-full" min={0} step={0.01} precision={2} />
              </Form.Item>
              <Form.Item
                name="orderReference"
                label="Order Reference"
                rules={[
                  { required: true, message: "Required" },
                  {
                    max: ORDER_REFERENCE_MAX_LENGTH,
                    message: `Max ${ORDER_REFERENCE_MAX_LENGTH} characters`,
                  },
                ]}
              >
                <Input maxLength={ORDER_REFERENCE_MAX_LENGTH} showCount />
              </Form.Item>
              <Form.Item name="collectionLocation" label="Collection">
                <Input placeholder="Collection location" />
              </Form.Item>
              <Form.Item name="deliveryLocation" label="Delivery">
                <Input placeholder="Delivery location" />
              </Form.Item>
              <Form.Item
                name="description"
                label="Description"
                rules={[
                  { required: true, message: "Required" },
                  {
                    max: DESCRIPTION_MAX_LENGTH,
                    message: `Max ${DESCRIPTION_MAX_LENGTH} characters`,
                  },
                ]}
              >
                <Input maxLength={DESCRIPTION_MAX_LENGTH} showCount />
              </Form.Item>
              <Form.Item
                className="md:col-span-2"
                name="externalBillToText"
                label="Bill To"
                rules={[{ required: true, message: "Required" }]}
              >
                <Input.TextArea rows={3} placeholder="Bill to details" />
              </Form.Item>
              <Form.Item
                className="md:col-span-2"
                name="serviceRecipientText"
                label={
                  <div className="flex w-full items-center justify-between gap-2">
                    <span>Service Recipient</span>
                    <span className="flex items-center gap-2">
                      <Button size="small" onClick={handleCopyCustomerToServiceRecipient}>
                        Copy Customer
                      </Button>
                      <Button size="small" onClick={handleClearServiceRecipient}>
                        Clear
                      </Button>
                    </span>
                  </div>
                }
              >
                <Input.TextArea rows={2} placeholder="Optional service recipient" />
              </Form.Item>
              <Form.Item
                className="md:col-span-2"
                name="bottomNote"
                label="Bottom Note"
                rules={[
                  {
                    max: BOTTOM_NOTE_MAX_LENGTH,
                    message: `Max ${BOTTOM_NOTE_MAX_LENGTH} characters`,
                  },
                ]}
              >
                <Input.TextArea
                  rows={2}
                  maxLength={BOTTOM_NOTE_MAX_LENGTH}
                  showCount
                  placeholder="Optional note on PDF"
                />
              </Form.Item>
            </div>
          </>
        )}

        <Divider orientation="left" className={createMode ? undefined : "!mt-0"}>
          Line items
        </Divider>

        <Form.List name="items">
          {(fields, { add, remove }) => (
            <div className="space-y-2">
              {fields.map(({ key, name, ...rest }) => {
                const row = itemsWatch?.[name] || {};
                const lineAmount = asCreditNegative(
                  Math.abs(Number(row?.qty || 0)) * Math.abs(Number(row?.price || 0))
                );

                return (
                  <div
                    key={key}
                    className="grid grid-cols-12 gap-2 items-end rounded-lg border border-slate-200 bg-white p-2"
                  >
                    <Form.Item
                      className="col-span-5 !mb-0"
                      {...rest}
                      name={[name, "description"]}
                      label="Description"
                      rules={[
                        { required: true, message: "Required" },
                        {
                          max: DESCRIPTION_MAX_LENGTH,
                          message: `Max ${DESCRIPTION_MAX_LENGTH} characters`,
                        },
                      ]}
                    >
                      <Input
                        placeholder="e.g. Discount / correction"
                        maxLength={DESCRIPTION_MAX_LENGTH}
                        showCount
                      />
                    </Form.Item>
                    <Form.Item
                      className="col-span-2 !mb-0"
                      {...rest}
                      name={[name, "qty"]}
                      label="Qty (−)"
                      rules={[{ required: true, message: "Required" }]}
                    >
                      <InputNumber className="w-full" step={1} />
                    </Form.Item>
                    <Form.Item
                      className="col-span-2 !mb-0"
                      {...rest}
                      name={[name, "price"]}
                      label="Price (−)"
                      rules={[{ required: true, message: "Required" }]}
                    >
                      <InputNumber className="w-full" step={0.01} precision={2} />
                    </Form.Item>
                    <div className="col-span-2 pb-1">
                      <div className="text-xs text-slate-500 mb-1">Amount (−)</div>
                      <div className="h-8 flex items-center font-medium text-red-600">
                        {lineAmount.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}{" "}
                        {currency}
                      </div>
                    </div>
                    <Form.Item label=" " colon={false} className="col-span-1 !mb-0">
                      <Button
                        danger
                        icon={<MinusOutlined />}
                        onClick={() => remove(name)}
                        disabled={fields.length <= 1}
                        className="w-full"
                        title="Remove line"
                      />
                    </Form.Item>
                  </div>
                );
              })}

              <Button
                type="dashed"
                icon={<PlusOutlined />}
                onClick={() =>
                  add({
                    description: "",
                    qty: -1,
                    price: 0,
                  })
                }
                block
                className="!border-[#053F92] !text-[#053F92]"
              >
                Add line item (discount / other)
              </Button>
            </div>
          )}
        </Form.List>

        <Divider />

        <div className="rounded-md border border-slate-200 bg-slate-50 p-3 mb-4 text-sm">
          <div className="flex justify-between mb-1">
            <span>Total without VAT</span>
            <span>
              {totals.subTotal.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}{" "}
              {currency}
            </span>
          </div>
          <div className="flex justify-between mb-1">
            <span>
              Total VAT
              {totals.vatPercentage > 0 ? ` (${totals.vatPercentage}%)` : ""}
            </span>
            <span>
              {totals.vatAmount.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}{" "}
              {currency}
            </span>
          </div>
          {Math.abs(totals.insurance) > 0 && (
            <div className="flex justify-between mb-1">
              <span>Insurance</span>
              <span>
                {totals.insurance.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}{" "}
                {currency}
              </span>
            </div>
          )}
          <div className="flex justify-between font-semibold text-base mt-2 pt-2 border-t border-slate-200">
            <span>Invoice total</span>
            <span className="text-red-600">
              {totals.total.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}{" "}
              {currency}
            </span>
          </div>
        </div>

        <Space className="w-full justify-end">
          <Button onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="primary" htmlType="submit" loading={busy}>
            Create Credit Invoice
          </Button>
        </Space>
      </Form>
    </Modal>
  );
};

export default CreditInvoiceModal;
